<template>
  <v-autocomplete
    class="font-size-14 caption white insights-filter"
    clearable
    dense
    hide-details
    item-color="white"
    open-on-clear
    outlined
    :data-tourid="`insights-select-${id}`"
    :filter="filterItem"
    :items="options"
    :label="label"
    :loading="loading"
    :menu-props="{ offsetY: true, maxWidth: 430 }"
    :multiple="multiple"
    :no-data-text="$t('error.no_data_available')"
    :no-filter="noFilter"
    :placeholder="$t('menu.selection.select_an_option')"
    :prepend-inner-icon="icon"
    :value="value"
    @update:search-input="$emit('search', $event)"
    @change="$emit('change', $event)"
  >
    <template v-slot:selection="{ item, index }">
      <div v-if="!multiple" style="max-width: 170px;">
        <p v-snip="1" class="ma-0 pa-0" :title="item.text">
          {{ item.text }}
        </p>
      </div>

      <div v-else>
        <v-chip
          v-if="index < 1"
          class="mt-1"
          outlined
          small
          style="max-width: 200px;"
          :key="`${value}-${item.value}`"
        >
          <div style="max-width: 150px;">
            <p v-snip="1" class="ma-0 pa-0 text-truncate" :title="item.text">
              {{ item.text }}
            </p>
          </div>
        </v-chip>

        <v-tooltip v-if="index === 1" bottom max-width="400px" :z-index="2">
          <template v-slot:activator="{ on }">
            <v-chip
              v-if="value.length"
              v-on="on"
              class="mt-1"
              outlined
              small
              style="z-index: 1"
            >
              + {{ value.length - 1 }}
            </v-chip>
          </template>

          <ul class="ma-0 pa-0 ms-2">
            <li
              v-for="option in textOptions"
              :key="option.id"
              class="ma-0 pa-0"
            >
              {{ option.text }}
            </li>
          </ul>
        </v-tooltip>
      </div>
    </template>

    <div
      v-if="removable"
      slot="prepend-item"
      class="d-flex justify-end pe-3 py-0 my-0"
    >
      <Menu
        :options="[
          {
            title: $t('insights.delete_filter'),
            id: 'deleteFilter',
            icon: 'mdi-delete-outline',
          },
        ]"
        @click="$emit('delete')"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            small
            icon
            class="blue-grey--text text--darken-1 py-0 my-0"
          >
            <v-icon size="24" class="blue-grey--text text--darken-1 py-0 my-0"
              >mdi-dots-horizontal</v-icon
            >
          </v-btn>
        </template>
      </Menu>
    </div>

    <template v-slot:item="data">
      <template v-if="multiple">
        <v-checkbox
          hide-details
          class="ma-0 pa-0 py-1"
          color="blue"
          :key="data.item.value"
          :ripple="false"
          :value="checkInputValue(data.item.value)"
          :input-value="checkInputValue(data.item.value)"
        >
          <template v-slot:label>
            <v-list-item-content class="mx-0 px-0 ms-2">
              <v-list-item-title
                class="d-flex mx-0 px-0 blue-grey--text text--darken-3 text-truncate"
              >
                <p
                  class="text-truncate mb-1"
                  style="max-width: 350px;"
                  :title="data.item.text"
                >
                  {{ data.item.text }}
                </p>
              </v-list-item-title>

              <v-list-item-subtitle
                v-if="data.item.secondText"
                class="primary--text mb-1"
                >{{ data.item.secondText }}</v-list-item-subtitle
              >

              <v-list-item-subtitle
                class="font-size-12 blue-grey--text d-flex align-center"
              >
                <v-icon
                  v-if="data.item.subtitleIcon"
                  class="me-1"
                  color="blue-grey"
                  size="12"
                  >{{ data.item.subtitleIcon }}</v-icon
                >
                {{ data.item.subtitle }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-checkbox>
      </template>

      <v-list-item-content v-else>
        <v-list-item-title
          class="mx-0 px-0 blue-grey--text text--darken-3 mb-1"
        >
          <span :title="data.item.text">{{ data.item.text }}</span>
        </v-list-item-title>

        <v-list-item-subtitle
          class="font-size-12 blue-grey--text d-flex align-center"
        >
          <v-icon
            color="blue-grey"
            size="12"
            class="me-1"
            v-if="data.item.subtitleIcon"
            >{{ data.item.subtitleIcon }}</v-icon
          >
          {{ data.item.subtitle }}
        </v-list-item-subtitle>
      </v-list-item-content>
    </template>

    <template v-if="helperText" v-slot:append-outer>
      <v-tooltip bottom max-width="200px" :z-index="10">
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" class="mr-2" color="blue-grey" size="18"
            >mdi-information-outline</v-icon
          >
        </template>
        <span class="font-size-14">{{ helperText }}</span>
      </v-tooltip>
    </template>
  </v-autocomplete>
</template>

<script>
import Menu from '@/components/UI/Menu.vue'
import _ from 'lodash'

const DIACRITICAL_ACCENT_REGEX = /[\u0300-\u036f]/g

export default {
  name: 'InsightsAutocomplete',

  props: {
    customLabel: String,
    filterLabelsKeys: Object,
    helperText: String,
    id: String,
    loading: Boolean,
    multiple: Boolean,
    noFilter: Boolean,
    options: Array,
    removable: Boolean,
    value: { required: false },
  },

  components: {
    Menu,
  },

  data() {
    return {
      iconsByFilterLevel: {
        siteGroups: 'mdi-office-building',
        countryCodes: 'mdi-earth',
        sites: 'mdi-warehouse',
      },
    }
  },

  computed: {
    icon() {
      return this.iconsByFilterLevel[this.id]
    },

    label() {
      return (
        this.customLabel ||
        _.capitalize(this.$t(`common.${this.filterLabelsKeys[this.id]}`))
      )
    },

    textOptions() {
      return this.options?.filter?.(option => {
        return this.value?.some?.(id => id === option?.value) ?? null
      })
    },
  },

  methods: {
    checkInputValue(inputValue) {
      const ids = this.value?.map?.(item => item?.value ?? item)
      return ids?.includes?.(inputValue)
    },

    normalizeText(text) {
      return text
        .toLowerCase()
        .normalize('NFD')
        .replace(DIACRITICAL_ACCENT_REGEX, '')
    },

    filterItem(_item, queryText, itemText) {
      const search = this.normalizeText(queryText)
      const text = this.normalizeText(itemText)
      return text.includes(search)
    },
  },
}
</script>

<style lang="scss">
.insights-filter.v-select.v-text-field {
  input {
    max-width: 50px;
  }
}
</style>
